import React from "react";

/**
 * Local error boundary — swallows the render exception and shows a
 * safe diagnostic in its place. Kept scoped: wrap around expandable
 * detail rows / heavy panels so a single malformed row can never
 * take down the entire AI Quality page (React error #31 etc.).
 *
 * Never suppresses backend errors — those are still surfaced via toast
 * / panel error states. This is a *last line* against renderer bugs.
 */
export default class PanelErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { error: null };
  }
  static getDerivedStateFromError(error) {
    return { error };
  }
  componentDidCatch(error, info) {
    if (typeof console !== "undefined" && console.error)
      console.error("[PanelErrorBoundary]", this.props.name || "panel", error, info);
  }
  render() {
    if (this.state.error) {
      const msg = (this.state.error?.message || String(this.state.error)).slice(0, 240);
      return (
        <div className="p-3 border border-signal-yellow/40 bg-signal-yellow/[0.05] text-[10px] mono"
             data-testid={`panel-error-${(this.props.name || "panel").replace(/\s+/g, "-")}`}>
          <div className="text-signal-yellow uppercase tracking-widest text-[9px] mb-1">
            Unable to render this field
          </div>
          <div className="text-muted-foreground">
            {this.props.name || "Panel"} hit a render error. Other AI Quality panels are unaffected.
          </div>
          <div className="mt-1 text-muted-foreground/60">{msg}</div>
          {this.props.onRetry && (
            <button
              className="mt-2 chip !text-[9px] hover:border-foreground"
              onClick={() => { this.setState({ error: null }); this.props.onRetry(); }}
              data-testid={`panel-error-retry-${(this.props.name || "panel").replace(/\s+/g, "-")}`}
            >Retry</button>
          )}
        </div>
      );
    }
    return this.props.children;
  }
}
